import { useState, useEffect } from 'react'

export interface DocPageFreshness {
  path: string
  originalUrl: string
  ourDate: string
  title: string
  originalDate: string | null
  isOutdated: boolean | null
  contentChanged?: boolean | null
  error?: string
}

export interface DocsFreshnessData {
  pages: DocPageFreshness[]
  lastChecked: string
  hasOutdated: boolean
}

// Hook to check freshness of all Discord docs
export function useDiscordDocsFreshness() {
  const [data, setData] = useState<DocsFreshnessData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    async function fetchFreshness() {
      try {
        const response = await fetch('/api/discord-docs/freshness', { signal: controller.signal })
        if (!response.ok) {
          throw new Error('Failed to fetch freshness data')
        }
        const freshnessData = await response.json()
        if (!controller.signal.aborted) setData(freshnessData)
      } catch (err) {
        if (!controller.signal.aborted)
          setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    void fetchFreshness()
    return () => controller.abort()
  }, [])

  return { data, loading, error }
}

// Hook to check freshness of a specific page
export function usePageFreshness(pagePath: string) {
  const [pageData, setPageData] = useState<DocPageFreshness | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    async function fetchPageFreshness() {
      try {
        // Remove leading slash for the API call
        const apiPath = pagePath.startsWith('/') ? pagePath.slice(1) : pagePath
        const response = await fetch(`/api/discord-docs/freshness/${apiPath}`, {
          signal: controller.signal,
        })

        if (!response.ok) {
          if (response.status === 404) {
            // Page not in our tracking list
            if (!controller.signal.aborted) setPageData(null)
            return
          }
          throw new Error('Failed to fetch page freshness')
        }

        const data = await response.json()
        if (!controller.signal.aborted) setPageData(data)
      } catch (err) {
        if (!controller.signal.aborted)
          setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        if (!controller.signal.aborted) setLoading(false)
      }
    }

    setPageData(null)
    setError(null)
    setLoading(Boolean(pagePath))
    if (pagePath) {
      void fetchPageFreshness()
    }
    return () => controller.abort()
  }, [pagePath])

  return { pageData, loading, error }
}
